import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../service/sendRequest';
import callApi from '../../service/callApi';
import { changeLocalMessage, setEditableMessage, setIsDirty } from './activ8MessagesReducer';
import projectErrorHandler from '../../utils/projectErrorHandler';

const GENERATE_MESSAGES = 'activ8/GENERATE_MESSAGES';
const REPLY_ACTIV8_MESSAGES = 'activ8/REPLY_ACTIV8_MESSAGES';
const CLEAR_SESSION = 'activ8/CLEAR_SESSION';

const SAVE_ACTIV8_MESSAGE = 'activ8/SAVE_ACTIV8_MESSAGES';
const DELETE_ACTIV8_MESSAGE = 'activ8/DELETE_ACTIV8_MESSAGE';
const GET_ACTIV8_MESSAGE_LOGS = 'activ8/GET_ACTIV8_MESSAGE_LOGS';
const CREATE_ACTIV8_MESSAGE = 'activ8/CREATE_ACTIV8_MESSAGE';
const EDIT_ACTIV8_MESSAGE = 'activ8/EDIT_ACTIV8_MESSAGE';
const SAVE_MESSAGE = 'activ8/SAVE_MESSAGE';
const DELETE_MULTIPLE_ACTIV8_MESSAGES = 'activ8/DELETE_MULTIPLE_ACTIV8_MESSAGES';
const DELETE_SAVED_ACTIV8_MESSAGES = 'activ8/DELETE_SAVED_ACTIV8_MESSAGES';

const SEARCH_ACTIV8_MESSAGES = 'activ8/SEARCH_ACTIV8_MESSAGES';

const ADJUST_MESSAGES = 'activ8/ADJUST_MESSAGES';

export const generateActiv8Messages = createAsyncThunk(GENERATE_MESSAGES, async ({ values }, { rejectWithValue }) => {
  const { byPass, focusOn, url, fileText } = values;

  if (!url) {
    return rejectWithValue('Url is required');
  }

  const response = await sendRequest(
    (cancelToken) =>
      callApi.post(
        '/api/activ8/message/url',
        {
          byPass,
          focusOn,
          url,
          fileText,
        },
        { cancelToken },
      ),
    {
      onError: (e) => rejectWithValue(e),
    },
  );

  return response.data;
});

export const replyActiv8Messages = createAsyncThunk(
  REPLY_ACTIV8_MESSAGES,
  async ({ reply }, { rejectWithValue, getState }) => {
    const thread = getState().activ8Messages.threadId;
    const response = await sendRequest(
      () => callApi.post(`/api/activ8/message/reply?thread=${thread}&reply=${reply}`),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    return response.data;
  },
);

export const clearSession = createAsyncThunk(CLEAR_SESSION, async (_, { rejectWithValue, getState }) => {
  const threadId = getState().activ8Messages.threadId;
  await sendRequest(() => callApi.post('/api/activ8/message/clear', null, { params: { thread: threadId } }), {
    onError: (e) => rejectWithValue(e),
  });

  return [];
});

export const saveActiv8Message = createAsyncThunk(
  SAVE_ACTIV8_MESSAGE,
  async ({ id, message, projectId }, { rejectWithValue }) => {
    const response = await sendRequest(
      () => callApi.post('/api/activ8/message/save', [message], { params: { projectId } }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    return { id: id, savedMessage: response.data[0] };
  },
);

export const deleteActiv8Message = createAsyncThunk(
  DELETE_ACTIV8_MESSAGE,
  async ({ id, savedId }, { rejectWithValue }) => {
    if (savedId) {
      sendRequest(() => callApi.delete(`/api/activ8/message/${savedId}`), {
        onError: (e) => rejectWithValue(e),
      });
    }

    return id;
  },
);

export const deleteMultipleActiv8Messages = createAsyncThunk(
  DELETE_MULTIPLE_ACTIV8_MESSAGES,
  async (ids, { rejectWithValue }) => {
    await sendRequest(() => callApi.delete(`/api/activ8/message/delete/by/ids`, { data: ids }), {
      onError: (e) => rejectWithValue(e),
    });

    return ids;
  },
);

export const deleteSavedActiv8Messages = createAsyncThunk(
  DELETE_SAVED_ACTIV8_MESSAGES,
  async (_, { rejectWithValue, getState, dispatch }) => {
    const savedMessages = getState().activ8Messages.savedMessagesContent;
    const savedMessagesIds = savedMessages.map(({ id }) => id);

    dispatch(deleteMultipleActiv8Messages(savedMessagesIds));

    return [];
  },
);

export const getActiv8MessageLogs = createAsyncThunk(
  GET_ACTIV8_MESSAGE_LOGS,
  async ({ messageId }, { rejectWithValue }) => {
    const response = await sendRequest(() => callApi.get(`/api/activ8/message/history/${messageId}`), {
      onError: (e) => rejectWithValue(e),
    });

    return response.data;
  },
);

export const createActiv8Message = createAsyncThunk(
  CREATE_ACTIV8_MESSAGE,
  async ({ messages, projectId }, { rejectWithValue }) => {
    const messagesToCreate = messages.map(({ message }) => message);

    if (!messagesToCreate.length) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () =>
        callApi.post('/api/activ8/message/create', {
          messages: messagesToCreate,
          projectId,
        }),
      {
        successStatusCode: 201,
        onError: (e) => rejectWithValue(e),
      },
    );

    return response.data;
  },
);

export const editActiv8Message = createAsyncThunk(
  EDIT_ACTIV8_MESSAGE,
  async ({ id, message, savedId }, { rejectWithValue }) => {
    if (!savedId) {
      return rejectWithValue('Saved is required');
    }

    const response = await sendRequest(() => callApi.post(`/api/activ8/message/edit-message/${savedId}`, { message }), {
      onError: (e) => rejectWithValue(e),
    });

    return { localId: id, updatedMessage: response.data };
  },
);

export const saveMessageChanges = createAsyncThunk(SAVE_MESSAGE, async (_, { dispatch, getState, rejectWithValue }) => {
  const {
    editableMessage: { id, message, savedId },
  } = getState().activ8Messages;

  if (!savedId) {
    return rejectWithValue('Saved is required');
  }

  dispatch(changeLocalMessage({ id, message }));
  dispatch(setEditableMessage({ id: null, message: '' }));
  dispatch(setIsDirty(false));

  const response = await sendRequest(
    () =>
      callApi.post(`/api/activ8/message/edit-message/${savedId}`, {
        message,
      }),
    {
      onError: (e) => projectErrorHandler(e, dispatch, rejectWithValue),
    },
  );

  return { localId: id, updatedMessage: response.data };
});

export const MIN_SEARCH_QUERY_LENGTH = 3;

export const searchActiv8Messages = createAsyncThunk(
  SEARCH_ACTIV8_MESSAGES,
  async ({ id }, { rejectWithValue, getState }) => {
    const { sort, searchQuery } = getState().activ8Messages.savedTableSettings;

    const response = await sendRequest(
      (cancelToken) =>
        callApi.post(
          `/api/activ8/message/search?sort=${sort}&id=${id}`,
          {
            ...(searchQuery ? { word: searchQuery } : {}),
          },
          { cancelToken },
        ),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    return response.data;
  },
);

export const adjustMessages = createAsyncThunk(
  ADJUST_MESSAGES,
  async ({ message, ...platforms }, { rejectWithValue }) => {
    const requestBody = {
      message,

      platforms: Object.entries(platforms)
        .map(([platform, value]) => ({
          platform,
          names: value.map((item) => item.value),
        }))
        .filter(({ names }) => names.length > 0),
    };

    const response = await sendRequest(() => callApi.post(`/api/story/generate`, requestBody), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    return response.data;
  },
);
